import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { Grid, Typography, Popper, Autocomplete } from '@mui/material';

import {
  postCreateCompetitor,
  getSubsidiariesLottery,
} from '@shared/services/services';
import StyledTextField from '@shared/components/StyledTextField';
import SectionGrid from '@shared/components/SectionGrid';
import GenericDropdown from '@shared/components/GenericDropdown';
import { StyledButtonConfirmar } from '@shared/components/Buttons/styles';

import { sxForm } from './styles';

const DESTINATIONS = [
  { value: 'Argentina', id: 'Argentina' },
  { value: 'Brasil', id: 'Brasil' },
  { value: 'Caribe', id: 'Caribe' },
  { value: 'Estados Unidos', id: 'Estados Unidos' },
  { value: 'Europa', id: 'Europa' },
];

const hoy = new Date();
const date18 = new Date(hoy.getFullYear() - 18, hoy.getMonth(), hoy.getDate());
const formatedDate18 = `${date18.getFullYear()}-${(date18.getMonth() + 1)
  .toString()
  .padStart(2, '0')}-${date18.getDate().toString().padStart(2, '0')}`;

const buildInitialValues = () => ({
  first_name: '',
  last_name: '',
  date: '',
  dni: '',
  whatsapp: '',
  email: '',
  destination: '',
  selectedFranchise: '',
  isGitRegistered: 'yes',
});

const buildValidationSchema = askDestination =>
  Yup.object().shape({
    first_name: Yup.string().required('Nombre requerido'),
    last_name: Yup.string().required('Apellido requerido'),
    date: Yup.date()
      .required('Fecha de nacimiento requerida')
      .max(formatedDate18, 'Debes ser mayor de 18 años'),
    dni: Yup.string()
      .required('Documento requerido')
      .min(8, 'El DNI debe tener al menos 8 caracteres'),
    whatsapp: Yup.string().required('Whatsapp requerido'),
    email: Yup.string()
      .email('Formato inválido de email')
      .required('Email requerido'),
    destination: askDestination
      ? Yup.string().required('Destino requerido')
      : Yup.string().nullable(),
  });

// Nombre/Apellido/Fecha/DNI a media fila, Whatsapp/Email/Destino a fila
// completa (mobile); todos a 3 por fila en desktop; el botón siempre al final
// a fila completa. El slot del botón se corre según si el destino está activo.
const buildCustomLayout = askDestination => {
  const fieldSlots = askDestination ? 7 : 6;
  const layout = { xs: {}, md: {} };

  for (let slot = 1; slot <= fieldSlots; slot++) {
    layout.xs[slot] = slot <= 4 ? 6 : 12;
    layout.md[slot] = 4;
  }

  const buttonSlot = fieldSlots + 1;
  layout.xs[buttonSlot] = 12;
  layout.md[buttonSlot] = 12;

  return layout;
};

const CustomPopper = props => (
  <Popper
    {...props}
    style={{ marginTop: '60px', marginLeft: '30px' }}
    placement="bottom-start"
  />
);

const GitRegistrationFields = ({
  isGitRegistered,
  onGitRegisteredChange,
  selectedFranchise,
  onSelectFranchise,
  subsidiaries,
  onSearchChange,
  formik,
  sx,
}) => (
  <Grid container sx={sx.formStyle} style={{ marginBottom: '1em' }}>
    <Grid container>
      <Typography
        style={{ fontWeight: '600', fontSize: '1.2rem', color: '#000000' }}
      >
        Completá el formulario para participar
      </Typography>
    </Grid>

    <Grid item container style={{ display: 'flex' }} id="containertextfields">
      <Grid item xs={12} md={3}>
        <GenericDropdown
          items={[
            { id: 'yes', fragments: [{ text: 'Sí' }] },
            { id: 'no', fragments: [{ text: 'No' }] },
          ]}
          selectedItem={
            isGitRegistered
              ? {
                  id: isGitRegistered,
                  fragments: [
                    { text: isGitRegistered === 'yes' ? 'Sí' : 'No' },
                  ],
                }
              : null
          }
          onSelect={item => onGitRegisteredChange(item.id)}
          buttonLabel="¿Estás registrado en GIT?"
          hasArrow={true}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Autocomplete
          sx={{
            width: { xs: '100%', sm: '90%' },
            mt: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              height: { xs: 40, sm: 50 },
            },
            '& .MuiInputBase-input': {
              fontSize: 14,
              color: 'text.primary',
            },
            '& .MuiFormLabel-root': {
              fontSize: { xs: 12, sm: 14 },
            },
          }}
          fullWidth
          freeSolo
          disabled={isGitRegistered === 'no'}
          options={subsidiaries}
          value={selectedFranchise}
          onChange={(_, value, reason) => {
            if (reason) onSelectFranchise('', '');
            if (value?.name) onSelectFranchise(value, value.name);
          }}
          onInputChange={(_, value) => onSearchChange(value)}
          getOptionLabel={option =>
            option && option.name && option.address
              ? `${option.name} - ${option.address}`
              : ''
          }
          filterOptions={(options, state) =>
            state.inputValue.length < 3 ? [] : options
          }
          PopperComponent={CustomPopper}
          renderInput={params => (
            <StyledTextField
              {...params}
              name="selectedFranchise"
              formik={formik}
              externalError={isGitRegistered === 'yes'}
              placeholder="Escribí el nombre de tu agencia"
            />
          )}
        />
      </Grid>
    </Grid>
  </Grid>
);

const LegalBlock = ({ sx, description }) => (
  <Grid item container sx={sx.legalContainer}>
    <Typography sx={sx.legalTitle}>Legales</Typography>
    <Typography
      sx={sx.legalText}
      dangerouslySetInnerHTML={{ __html: description }}
    />
  </Grid>
);

const FinalizedCard = ({ sx, cardWidth }) => (
  <Grid
    container
    justifyContent="center"
    alignItems="center"
    style={{ width: '100%' }}
  >
    <Grid
      item
      xs={12}
      style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
    >
      <Grid
        container
        direction="column"
        alignItems="center"
        sx={sx.formStyle}
        style={{ width: cardWidth, textAlign: 'center' }}
      >
        <Typography
          style={{ fontWeight: '600', fontSize: '1.2rem', color: '#000000' }}
        >
          Sorteo finalizado
        </Typography>
        <Typography style={{ marginTop: '8px' }}>
          Gracias por participar.
        </Typography>
      </Grid>
    </Grid>
  </Grid>
);

const Formulary = ({
  sorteo,
  setStatusModalProp,
  setShowModalProp,
  showGitRegistration = false,
  isGitRegistered,
  setIsGitRegistered,
  cardWidth = '100%',
  legalInsideCard = true,
}) => {
  const { user } = useSelector(store => store.userInfo) ?? {};
  const sx = sxForm();

  const [search, setSearch] = useState('');
  const [selectedFranchise, setSelectedFranchise] = useState(
    user ? user.entity : null
  );
  const [subsidiaries, setSubsidiaries] = useState([]);

  const askDestination = Boolean(sorteo?.ask_destination);

  const fetchGetSubsidiaries = async () => {
    try {
      const result = await getSubsidiariesLottery(search);
      setSubsidiaries(result);
    } catch (error) {
      console.error('error', error);
    }
  };

  useEffect(() => {
    if (showGitRegistration && search.length >= 3) fetchGetSubsidiaries();
  }, [search]);

  const handleStatusPost = success => {
    setStatusModalProp(success);
    setShowModalProp(true);
  };

  const formik = useFormik({
    initialValues: buildInitialValues(),
    validationSchema: buildValidationSchema(askDestination),
    onSubmit: async (values, { resetForm }) => {
      try {
        const body = {
          nombre: values.first_name,
          apellido: values.last_name,
          email: values.email,
          whats_app: values.whatsapp,
          dni: values.dni,
          birthdate: values.date,
          id_sorteo: sorteo.id_sorteo,
          ...(askDestination ? { proximo_destino: values.destination } : {}),
          ...(showGitRegistration
            ? {
                is_registered: isGitRegistered === 'yes',
                id_entity: selectedFranchise?.id ?? null,
              }
            : {}),
        };

        const result = await postCreateCompetitor(body);

        if (result.id_participante) {
          handleStatusPost(true);
        }
      } catch (error) {
        handleStatusPost(false);
      } finally {
        resetForm();
      }
    },
  });

  const handleGitRegisteredChange = value => {
    setIsGitRegistered(value);
    formik.setFieldValue('isGitRegistered', value);
    formik.setFieldValue('selectedFranchise', '');
    setSelectedFranchise('');
  };

  const handleSelectFranchise = (franchise, formikValue) => {
    setSelectedFranchise(franchise);
    formik.setFieldValue('selectedFranchise', formikValue);
  };

  if (sorteo?.is_active === false) {
    return <FinalizedCard sx={sx} cardWidth={cardWidth} />;
  }

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        {showGitRegistration && !user && (
          <GitRegistrationFields
            isGitRegistered={isGitRegistered}
            onGitRegisteredChange={handleGitRegisteredChange}
            selectedFranchise={selectedFranchise}
            onSelectFranchise={handleSelectFranchise}
            subsidiaries={subsidiaries}
            onSearchChange={setSearch}
            formik={formik}
            sx={sx}
          />
        )}

        <Grid
          container
          sx={sx.formStyle}
          style={{ width: cardWidth, minHeight: '250px' }}
        >
          <Grid container>
            <Typography
              style={{
                fontWeight: '600',
                fontSize: '1.2rem',
                color: '#000000',
                marginBottom: '20px',
              }}
            >
              Información personal
            </Typography>
          </Grid>

          <SectionGrid
            gridSx={{ paddingBottom: 0, padding: { xs: '0' } }}
            customLayout={buildCustomLayout(askDestination)}
          >
            <StyledTextField name="first_name" label="Nombre" formik={formik} />
            <StyledTextField
              name="last_name"
              label="Apellido"
              formik={formik}
            />
            <StyledTextField
              formik={formik}
              name="date"
              label="Fecha de Nacimiento"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <StyledTextField name="dni" label="DNI" formik={formik} />
            <StyledTextField name="whatsapp" label="Whatsapp" formik={formik} />
            <StyledTextField name="email" label="Email" formik={formik} />

            {askDestination && (
              <GenericDropdown
                items={DESTINATIONS.map(d => ({
                  ...d,
                  fragments: [{ text: d.value }],
                }))}
                selectedItem={
                  formik.values.destination
                    ? {
                        id: formik.values.destination,
                        fragments: [{ text: formik.values.destination }],
                      }
                    : null
                }
                onSelect={item => formik.setFieldValue('destination', item.id)}
                buttonLabel="¿A qué destino querés viajar?"
                hasArrow={true}
              />
            )}

            <StyledButtonConfirmar
              onClick={formik.handleSubmit}
              fontSize={{ xs: '16px', sm: '20px' }}
              height={{ xs: '40px', sm: '50px' }}
              width={{ xs: '100%', sm: '25%' }}
              align="end"
            >
              Confirmar
            </StyledButtonConfirmar>
          </SectionGrid>

          {legalInsideCard && (
            <LegalBlock sx={sx} description={sorteo.description} />
          )}
        </Grid>

        {!legalInsideCard && (
          <LegalBlock sx={sx} description={sorteo.description} />
        )}
      </Grid>
    </Grid>
  );
};

export default Formulary;
